// src/pages/alerts/AlertsEnabled.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Search } from 'lucide-react';
import PageTitle from './components/PageTitle';
import AlertTypeToggle from './components/AlertTypeToggle';
import AlertCard from './components/AlertCard';
import EnabledCommodityCard from './components/EnabledCommodityCard';
import { useGlobalFontSize } from '../../context/GlobalFontSizeContext';
import {
  useGetDefaultAlerts,
  useGetCustomAlerts,
  useUpdateDefaultAlert,
  useUpdateCustomAlert,
  useDeleteDefaultAlert,
  useDeleteCustomAlert,
} from '../../utils/api-hooks/alerts';
import { ROUTES } from '../../constants/Routes';
import PageSkeleton from '../../components/PageSkeleton';

const formatCommodity = (commodity) => {
  if (!commodity) return '';
  return commodity.charAt(0).toUpperCase() + commodity.slice(1).toLowerCase();
};

const CommoditySelect = ({ value, options, onChange, allLabel }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const display = value === 'all' ? allLabel : value;

  return (
    <div className="relative shrink-0" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-2 rounded-[9px] border border-[#D5D8D6] dark:border-[#5d625f] bg-white dark:bg-[#1d1d1d] px-3 py-2 text-[13px] leading-none text-[#282A29] dark:text-white hover:bg-[#F9F9F9] dark:hover:bg-[#282a29] transition-colors"
      >
        <span className="font-['Inter'] whitespace-nowrap">{display}</span>
        <ChevronDown
          size={14}
          className={`text-[#828382] shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <div className="absolute left-0 z-30 mt-1 min-w-full overflow-hidden rounded-[9px] border border-[#D5D8D6] dark:border-[#5d625f] bg-white dark:bg-[#1d1d1d] shadow-lg">
          <button
            type="button"
            onClick={() => {
              onChange('all');
              setOpen(false);
            }}
            className={`block w-full whitespace-nowrap px-3 py-2 text-left font-['Inter'] text-[13px] transition-colors ${
              value === 'all'
                ? 'bg-[#E3F5EB] text-[#12823F]'
                : 'text-[#282A29] dark:text-white hover:bg-[#F0F5F2] dark:hover:bg-[#282a29]'
            }`}
          >
            {allLabel}
          </button>
          {options.map((opt) => (
            <button
              key={opt}
              type="button"
              onClick={() => {
                onChange(opt);
                setOpen(false);
              }}
              className={`block w-full whitespace-nowrap px-3 py-2 text-left font-['Inter'] text-[13px] transition-colors ${
                value === opt
                  ? 'bg-[#E3F5EB] text-[#12823F]'
                  : 'text-[#282A29] dark:text-white hover:bg-[#F0F5F2] dark:hover:bg-[#282a29]'
              }`}
            >
              {opt}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const AlertsEnabled = ({ embedded = false, onCreateAlert }) => {
  const { t } = useTranslation();
  const { fontSize } = useGlobalFontSize();
  const navigate = useNavigate();
  const [alertType, setAlertType] = useState('custom'); // 'smart' or 'custom' (standalone page)
  const [searchQuery, setSearchQuery] = useState('');
  const [commodityFilter, setCommodityFilter] = useState('all');
  const [collapsedCommodities, setCollapsedCommodities] = useState({});

  const {
    data: defaultAlertsData,
    isLoading: isLoadingDefault,
    isFetching: isFetchingDefault,
    isError: isErrorDefault,
    error: errorDefault,
    refetch: refetchDefault,
  } = useGetDefaultAlerts();

  const {
    data: customAlertsData,
    isLoading: isLoadingCustom,
    isFetching: isFetchingCustom,
    isError: isErrorCustom,
    error: errorCustom,
    refetch: refetchCustom,
  } = useGetCustomAlerts();

  const updateDefaultMutation = useUpdateDefaultAlert();
  const updateCustomMutation = useUpdateCustomAlert();
  const deleteDefaultMutation = useDeleteDefaultAlert();
  const deleteCustomMutation = useDeleteCustomAlert();

  const isLoading = embedded
    ? isLoadingDefault || isLoadingCustom
    : alertType === 'smart'
      ? isLoadingDefault
      : isLoadingCustom;
  const isFetching = embedded
    ? isFetchingDefault || isFetchingCustom
    : alertType === 'smart'
      ? isFetchingDefault
      : isFetchingCustom;
  // Embedded hub shows whichever list loaded. Only fail the page if both do.
  const isError = embedded
    ? isErrorDefault && isErrorCustom
    : alertType === 'smart'
      ? isErrorDefault
      : isErrorCustom;
  const error = embedded
    ? errorDefault || errorCustom
    : alertType === 'smart'
      ? errorDefault
      : errorCustom;

  const handleAlertTypeChange = (newAlertType) => {
    setAlertType(newAlertType);
  };

  const handleToggleAlert = (alert) => {
    const type = alert._sourceType || alertType;
    if (type === 'smart') {
      updateDefaultMutation.mutate(alert);
    } else {
      updateCustomMutation.mutate(alert);
    }
  };

  const handleDeleteAlert = (alert) => {
    const type = alert._sourceType || alertType;
    if (type === 'smart') {
      deleteDefaultMutation.mutate(alert);
    } else {
      deleteCustomMutation.mutate(alert);
    }
  };

  const handleEditAlert = (alert) => {
    const type = alert._sourceType || alertType;
    navigate(`/alerts/edit/${alert.alert_id}?type=${type}`);
  };

  const alerts = useMemo(() => {
    if (embedded) {
      const smart = (defaultAlertsData?.alerts || []).map((a) => ({
        ...a,
        _sourceType: 'smart',
      }));
      const custom = (customAlertsData?.alerts || []).map((a) => ({
        ...a,
        _sourceType: 'custom',
      }));
      return [...smart, ...custom];
    }
    if (alertType === 'smart') {
      return defaultAlertsData?.alerts || [];
    }
    return customAlertsData?.alerts || [];
  }, [embedded, alertType, defaultAlertsData, customAlertsData]);

  const filteredAlerts = useMemo(() => {
    let result = alerts;
    if (commodityFilter !== 'all') {
      result = result.filter(
        (a) => formatCommodity(a.commodity) === commodityFilter
      );
    }
    const q = searchQuery.trim().toLowerCase();
    if (!q) return result;
    return result.filter((a) => {
      const commodity = String(a.commodity || '').toLowerCase();
      const type = String(a.specific_alert_type || a.alert_type || '').toLowerCase();
      return commodity.includes(q) || type.includes(q);
    });
  }, [alerts, commodityFilter, searchQuery]);

  const commodityOptions = useMemo(() => {
    const names = new Set();
    alerts.forEach((a) => {
      const name = formatCommodity(a.commodity);
      if (name) names.add(name);
    });
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [alerts]);

  const commodityCount = commodityOptions.length;

  const groupedByCommodity = useMemo(() => {
    const groups = {};
    filteredAlerts.forEach((alert) => {
      const key = formatCommodity(alert.commodity) || 'Other';
      if (!groups[key]) groups[key] = [];
      groups[key].push(alert);
    });
    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
  }, [filteredAlerts]);

  const leftColumnAlerts = filteredAlerts.filter((_, index) => index % 2 === 0);
  const rightColumnAlerts = filteredAlerts.filter((_, index) => index % 2 === 1);

  const isAlertToggling = (alert) => {
    const type = alert._sourceType || alertType;
    if (type === 'smart') {
      return updateDefaultMutation.isPending && updateDefaultMutation.variables?.alert_id === alert.alert_id;
    }
    return updateCustomMutation.isPending && updateCustomMutation.variables?.alert_id === alert.alert_id;
  };

  const isAlertDeleting = (alert) => {
    const type = alert._sourceType || alertType;
    if (type === 'smart') {
      return deleteDefaultMutation.isPending && deleteDefaultMutation.variables?.alert_id === alert.alert_id;
    }
    return deleteCustomMutation.isPending && deleteCustomMutation.variables?.alert_id === alert.alert_id;
  };

  const shellClass = embedded
    ? 'flex flex-col w-full gap-4'
    : 'w-full h-full flex flex-col items-center justify-start px-4 py-10 bg-[#F0F5F2] bg-dark-app';

  const innerClass = embedded
    ? 'flex flex-col gap-4 w-full'
    : 'flex flex-col gap-10 items-center w-full max-w-full lg:max-w-[1038px]';

  const renderHeader = () => {
    if (embedded) {
      return (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <h2 className="font-['Inter'] font-bold uppercase tracking-[0.06em] text-[#282A29] dark:text-white text-sm">
              {t('alerts.hub.enabledSectionTitle')}
            </h2>
            <p className="mt-2 font-['Inter'] text-[14px] leading-snug text-[#282A29] dark:text-white">
              {t('alerts.hub.enabledSectionSummary', {
                alertCount: alerts.length,
                commodityCount,
              })}
            </p>
            <p className="mt-1 font-['Inter'] text-[14px] leading-snug text-[#5D625F] dark:text-[#C3C4C3]">
              {t('alerts.hub.enabledSectionSubtitle')}
            </p>
            {embedded && (isErrorDefault || isErrorCustom) && !(isErrorDefault && isErrorCustom) ? (
              <p className="mt-1 font-['Inter'] text-[13px] text-red-500">
                {isErrorCustom
                  ? t('alerts.hub.customLoadError', 'Custom alerts failed to load.')
                  : t('alerts.hub.smartLoadError', 'Smart alerts failed to load.')}
              </p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={() => {
              if (onCreateAlert) onCreateAlert();
              else navigate(ROUTES.ALERTS_CONFIGURE);
            }}
            className="shrink-0 rounded-[9px] bg-bloom-green px-4 py-2 font-['Inter'] font-medium text-white hover:bg-bloom-green-hover transition-colors self-start"
            style={{ fontSize }}
          >
            {t('alerts.hub.createButton')}
          </button>
        </div>
      );
    }
    return <PageTitle title="Alerts enabled" />;
  };

  if (isLoading) {
    return (
      <div className={shellClass}>
        <div className={innerClass}>
          {renderHeader()}
          {!embedded && <AlertTypeToggle alertType={alertType} onAlertTypeChange={handleAlertTypeChange} />}
          <PageSkeleton shape="alerts" />
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className={shellClass}>
        <div className={innerClass}>
          {renderHeader()}
          {!embedded && <AlertTypeToggle alertType={alertType} onAlertTypeChange={handleAlertTypeChange} />}
          <div className="w-full flex justify-center py-20">
            <div className="flex flex-col items-center gap-4">
              <p
                className="text-red-500 font-['Inter']"
                style={{ fontSize: fontSize + 2 }}
              >
                Failed to load alerts: {error?.message || 'Unknown error'}
              </p>
              <button
                onClick={() => {
                  refetchDefault();
                  refetchCustom();
                }}
                className="px-6 py-2 bg-bloom-green text-white rounded-lg hover:bg-bloom-green-hover transition-colors"
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (embedded) {
    return (
      <div className={shellClass}>
        <div className={innerClass}>
          {renderHeader()}

          <div className="flex flex-wrap items-center gap-2">
            <CommoditySelect
              value={commodityFilter}
              options={commodityOptions}
              onChange={setCommodityFilter}
              allLabel={t('alerts.hub.allCommodity')}
            />
            <div className="relative w-full sm:w-auto sm:min-w-[240px] sm:max-w-md flex-1 sm:flex-initial">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#828382]" />
              <input
                type="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('alerts.hub.searchPlaceholder')}
                className="w-full rounded-[9px] border border-[#D5D8D6] dark:border-[#5d625f] bg-white dark:bg-[#1d1d1d] pl-9 pr-3 py-2 text-[13px] leading-none text-[#282A29] dark:text-white placeholder:text-[#828382] focus:outline-none focus:border-bloom-green"
              />
            </div>
          </div>

          {isFetching && !isLoading && (
            <div className="flex items-center gap-2">
              <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-bloom-green"></div>
              <p className="text-[#828382] dark:text-[#888888] font-['Inter']" style={{ fontSize }}>
                Updating...
              </p>
            </div>
          )}

          {groupedByCommodity.length === 0 ? (
            <div className="w-full flex justify-center py-12">
              <p className="text-[#828382] dark:text-[#888888] font-['Inter'] text-center" style={{ fontSize: fontSize + 2 }}>
                {t('alerts.hub.noEnabledAlerts')}
              </p>
            </div>
          ) : (
            <div className="flex flex-col md:flex-row md:items-start gap-4 md:gap-6">
              {[0, 1].map((colIndex) => {
                const columnGroups = groupedByCommodity.filter((_, i) => i % 2 === colIndex);
                if (columnGroups.length === 0) return null;
                return (
                  <div key={colIndex} className="flex min-w-0 flex-1 flex-col gap-4">
                    {columnGroups.map(([commodity, commodityAlerts]) => (
                      <EnabledCommodityCard
                        key={commodity}
                        commodity={commodity}
                        alerts={commodityAlerts}
                        collapsed={Boolean(collapsedCommodities[commodity])}
                        onToggleCollapse={() =>
                          setCollapsedCommodities((prev) => ({
                            ...prev,
                            [commodity]: !prev[commodity],
                          }))
                        }
                        onToggleAlert={handleToggleAlert}
                        onDeleteAlert={handleDeleteAlert}
                        onEditAlert={handleEditAlert}
                        isAlertToggling={isAlertToggling}
                        isAlertDeleting={isAlertDeleting}
                      />
                    ))}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  }

  if (alerts.length === 0) {
    return (
      <div className={shellClass}>
        <div className={innerClass}>
          {renderHeader()}
          <AlertTypeToggle alertType={alertType} onAlertTypeChange={handleAlertTypeChange} />
          <div className="w-full flex justify-center py-20">
            <div className="flex flex-col items-center gap-4">
              <p
                className="text-[#828382] dark:text-[#888888] font-['Inter'] text-center"
                style={{ fontSize: fontSize + 4 }}
              >
                No {alertType === 'smart' ? 'smart' : 'custom'} alerts found.
              </p>
              <p
                className="text-[#828382] dark:text-[#666666] font-['Inter'] text-center"
                style={{ fontSize: fontSize }}
              >
                Create a new alert to get started.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={shellClass}>
      <div className={innerClass}>
        {renderHeader()}
        <AlertTypeToggle alertType={alertType} onAlertTypeChange={handleAlertTypeChange} />

        {isFetching && !isLoading && (
          <div className="flex items-center gap-2">
            <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-bloom-green"></div>
            <p
              className="text-[#828382] dark:text-[#888888] font-['Inter']"
              style={{ fontSize: fontSize }}
            >
              Updating...
            </p>
          </div>
        )}

        <div className="w-full flex justify-center">
          <div className="w-full max-w-[386px] md:hidden flex flex-col gap-4">
            {filteredAlerts.map((alert) => (
              <AlertCard
                key={alert.alert_id}
                alert={alert}
                onToggle={handleToggleAlert}
                onDelete={handleDeleteAlert}
                onEdit={handleEditAlert}
                isToggling={isAlertToggling(alert)}
                isDeleting={isAlertDeleting(alert)}
              />
            ))}
          </div>

          <div className="hidden md:flex w-full max-w-[820px] gap-[48px]">
            <div className="flex-1 flex flex-col gap-4">
              {leftColumnAlerts.map((alert) => (
                <AlertCard
                  key={alert.alert_id}
                  alert={alert}
                  onToggle={handleToggleAlert}
                  onDelete={handleDeleteAlert}
                  onEdit={handleEditAlert}
                  isToggling={isAlertToggling(alert)}
                  isDeleting={isAlertDeleting(alert)}
                />
              ))}
            </div>
            <div className="flex-1 flex flex-col gap-4">
              {rightColumnAlerts.map((alert) => (
                <AlertCard
                  key={alert.alert_id}
                  alert={alert}
                  onToggle={handleToggleAlert}
                  onDelete={handleDeleteAlert}
                  onEdit={handleEditAlert}
                  isToggling={isAlertToggling(alert)}
                  isDeleting={isAlertDeleting(alert)}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AlertsEnabled;
